import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { CTABand } from "@/components/shared/CTABand";
import { useConsultation } from "@/components/forms/ConsultationDialog";
import { PRACTICES, INDUSTRIES, IMAGES } from "@/data/content";

export default function Solutions() {
  const { open } = useConsultation();
  return (
    <>
      <PageHero
        kicker="Practice Areas"
        title="Full-stack IT sourcing. One independent advocate."
        subtitle="Six signature practices, each launched with an exhaustive, provider-agnostic assessment. Sourced across 600+ providers — 100% agnostic, always."
        image={IMAGES.dataCenter}
      >
        <Button variant="hero" size="xl" onClick={open}>Book a Practice Assessment <ArrowRight className="h-5 w-5" /></Button>
      </PageHero>

      <section className="section-x py-20 sm:py-28">
        <div className="container-tmt">
          <RevealStagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PRACTICES.map((p) => {
              const Icon = p.icon;
              return (
                <RevealItem key={p.slug}>
                  <div className="card-hairline group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground">
                        <Icon className="h-6 w-6" />
                      </span>
                      <span className="rounded-md border border-primary/20 bg-primary/5 px-2.5 py-1 font-mono text-xs text-primary">{p.tool}</span>
                    </div>
                    <h3 className="mt-5 font-display text-xl font-semibold">{p.name}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                    <button onClick={open} className="mt-5 inline-flex w-fit items-center gap-1.5 font-mono text-xs text-primary transition-colors hover:text-primary-glow">
                      Start assessment <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </RevealItem>
              );
            })}
          </RevealStagger>
        </div>
      </section>

      {/* Industries */}
      <section className="section-x pb-8">
        <div className="container-tmt">
          <div className="grid items-center gap-10 rounded-3xl border border-border bg-gradient-surface p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <SectionHeading
                kicker="Industry Specialization"
                title="Deep context where the stakes are highest"
                subtitle="We bring vertical fluency to the regulations, workloads, and buying patterns that define your industry."
              />
              <Reveal delay={0.15}>
                <Button variant="outline" size="lg" asChild className="mt-8">
                  <Link to="/what-changed">Explore situational triggers <ArrowUpRight className="h-4 w-4" /></Link>
                </Button>
              </Reveal>
            </div>
            <RevealStagger className="grid gap-4 sm:grid-cols-2">
              {INDUSTRIES.map((i) => (
                <RevealItem key={i}>
                  <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4">
                    <Check className="h-5 w-5 shrink-0 text-primary" />
                    <span className="font-display font-medium">{i}</span>
                  </div>
                </RevealItem>
              ))}
            </RevealStagger>
          </div>
        </div>
      </section>

      <CTABand title="Not sure which practice you need?" subtitle="Book a 25-minute consultation and we'll point you to the right specialist — no pitch, no obligation." />
    </>
  );
}
